import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { cardIconChain } from '@/lib/crCardIconChain';
import { qbotCardIconUrl } from '@/services/qbotApi';

interface CardIconProps {
  id: number;
  name?: string;
  /** 'base' (default), 'evolution', or 'hero' form */
  form?: 'base' | 'evolution' | 'hero';
  /**
   * Official Supercell CDN icon URL for this card+form (from the card's
   * `iconUrls`). Preferred when present — it's the canonical asset, globally
   * cached, and avoids the qbot proxy hop (which 404s for some cards, e.g.
   * Bowler, falling back to the text placeholder). Falls back to the qbot
   * proxy when absent.
   */
  iconUrl?: string;
  /** Display level (1-14) — rendered as an overlay badge if provided */
  level?: number;
  /** Tailwind size class — defaults to a 72px square that fits the 4x2 grid */
  className?: string;
  /** Optional click handler — used by deck builder to swap a card */
  onClick?: () => void;
}

/**
 * Single card icon rendered from qbot-api `/api/v1/cr/cards/:id/icon`.
 *
 * The PNG is served with `Cache-Control: public, max-age=604800, immutable`
 * so we let the browser cache it — no extra in-app cache layer needed.
 *
 * On image error we fall back along a CHAIN (`lib/crCardIconChain.ts`, I-363): the official `iconUrl`, then the qbot proxy for the
 * asked form, then — for a hero/evolution — the proxy's BASE image (the ring stays, and says the form), and only then the card name
 * as text. Real Clash Royale cards rarely go missing, but evolution/hero forms aren't always synced on the server side (stg-qbot
 * lacks Ice Wizard HERO, Elite Barbarians EVOLUTION and all of Minion Giant) and would otherwise show a broken image icon. Each
 * step is tried once.
 */
export function CardIcon({
  id,
  name,
  form = 'base',
  iconUrl,
  level,
  className,
  onClick,
}: CardIconProps) {
  const { t } = useTranslation('cr');
  // La catena dei `src` da provare (`lib/crCardIconChain.ts`): ufficiale → proxy della forma → proxy della BASE. `passo` e' quanti
  // ne sono GIA' falliti per QUESTA catena: legato alla catena, cosi' un'altra carta (o un'altra forma) nello stesso componente
  // riparte dal primo e non eredita il fallimento di quella di prima.
  const chain = cardIconChain({ id, form, iconUrl, proxy: qbotCardIconUrl });
  const chainKey = chain.join('\n');
  const [fallimenti, setFallimenti] = useState<{ chainKey: string; passo: number }>({ chainKey, passo: 0 });
  const passo = fallimenti.chainKey === chainKey ? fallimenti.passo : 0;
  const interactive = Boolean(onClick);

  // Finiti i passi, e solo allora, il nome scritto.
  if (passo >= chain.length) {
    return (
      <div
        className={cn(
          'flex aspect-square items-center justify-center rounded-md bg-bg-tertiary text-[10px] text-fg-muted',
          className,
        )}
        title={name}
      >
        {name ?? `#${id}`}
      </div>
    );
  }

  const ringClass = form === 'hero'
    ? 'ring-2 ring-yellow-400/80'
    : form === 'evolution'
      ? 'ring-2 ring-fuchsia-400/80'
      : '';

  const content = (
    <div className={cn('relative aspect-square overflow-hidden rounded-md bg-bg-tertiary', ringClass, className)}>
      <img
        src={chain[passo]}
        alt={name ?? t('card.alt', { id })}
        loading="lazy"
        decoding="async"
        // Un errore fa passare al passo DOPO quello che l'ha dato: `passo + 1` e' calcolato dal passo di QUESTO render, non
        // incrementato dallo stato, cosi' se l'evento arriva due volte per lo stesso `src` (nello stesso batch) non si salta un passo.
        // Niente ciclo: oltre l'ultimo c'e' il nome.
        onError={() => setFallimenti({ chainKey, passo: passo + 1 })}
        className="h-full w-full object-contain"
      />
      {typeof level === 'number' && (
        <span
          className={cn(
            'absolute bottom-0.5 right-0.5 rounded px-1 text-[10px] font-bold tabular-nums leading-tight',
            level >= 14 ? 'bg-yellow-500 text-black' : 'bg-black/70 text-white',
          )}
        >
          {level}
        </span>
      )}
    </div>
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className="block w-full transition-transform active:scale-95">
        {content}
      </button>
    );
  }
  return content;
}
